import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

const COLORS = {
  CREATED: '#E2E8F0',    // Light gray
  IN_PROGRESS: '#3B82F6', // Blue
  COMPLETED: '#10B981'    // Green
};

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const STATUS_LABELS = {
  CREATED: 'Created',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed'
};

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner"></div>
    <p>Loading job status data...</p>
  </div>
);

const StatusLegend = ({ jobStatuses, totalJobs }) => (
  <div className="status-legend">
    {Object.entries(jobStatuses).map(([status, count]) => (
      <div key={status} className="legend-item">
        <div className="legend-color" style={{ backgroundColor: COLORS[status] }} />
        <span className="legend-label">{STATUS_LABELS[status]}</span>
        <span className="legend-count">
          {count} ({totalJobs ? Math.round((count / totalJobs) * 100) : 0}%)
        </span>
      </div>
    ))}
  </div>
);

const JobStatusCard = () => {
  const [jobStatuses, setJobStatuses] = useState({
    CREATED: 0,
    IN_PROGRESS: 0,
    COMPLETED: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('ALL'); // To filter jobs by status

  const fetchJobStatusCounts = async () => {
    setError('');
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Authentication required. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://jobmanagement-497524552274.us-central1.run.app';
      const requests = Object.keys(jobStatuses).map(status => {
        const headers = new Headers({
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        });

        return fetch(`${baseUrl}/api/jobs/status/${status}/count`, {
          method: 'GET',
          headers,
        }).then(response => {
          if (response.status === 403) {
            throw new Error('Permission denied. Please contact your administrator.');
          }
          if (response.status === 401) {
            localStorage.removeItem('authToken');
            throw new Error('Session expired. Please log in again.');
          }
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        });
      });

      const responses = await Promise.all(requests);
      const newStatuses = {};

      // Log the raw responses for debugging
      console.log('Job status responses:', responses);

      // Map the responses dynamically to the status keys
      const statusKeys = Object.keys(jobStatuses);

      responses.forEach((response, index) => {
        // Assign the corresponding value to the job status dynamically
        newStatuses[statusKeys[index]] = response; // Assuming response is the count for that status
      });

      // Log the updated statuses
      console.log('Updated job statuses:', newStatuses);

      setJobStatuses(newStatuses);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch job status data. Please try again.';
      console.error('Error fetching job status data:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      
      if (err.message?.includes('Session expired')) {
        // Trigger auth refresh or redirect to login
        // window.location.href = '/login';
      }
    }
  };

  useEffect(() => {
    fetchJobStatusCounts();
    const interval = setInterval(fetchJobStatusCounts, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const chartData = Object.entries(jobStatuses)
    .filter(([status]) => selectedStatus === 'ALL' || status === selectedStatus) // Apply filtering based on status
    .map(([status, count]) => ({
      name: STATUS_LABELS[status],
      value: count,
      status: status
    }));

  const totalJobs = Object.values(jobStatuses).reduce((a, b) => a + b, 0);

  return (
    <div className="job-status-card">
      <div className="card-header">
        <h2>Job Status Summary</h2>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchJobStatusCounts}
            disabled={loading}
          >
            Refresh
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="filter-container">
        <label htmlFor="statusFilter">Filter by status:</label>
        <select 
          id="statusFilter" 
          value={selectedStatus} 
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="CREATED">Created</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
        </select>
      </div>

      <div className="card-content">
        {error ? (
          <div className="error-message">
            {error}
          </div>
        ) : loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {chartData.map((entry) => (
                      <Cell 
                        key={entry.status} 
                        fill={COLORS[entry.status]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <StatusLegend jobStatuses={jobStatuses} totalJobs={totalJobs} />
          </>
        )}
      </div>

      <style jsx>{`
        .job-status-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
          padding: 20px;
          margin: 20px;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .refresh-button {
          padding: 6px 12px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .refresh-button:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .refresh-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .last-updated {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
        }

        .spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          color: #dc2626;
          padding: 12px;
          background: #fee2e2;
          border-radius: 4px;
          margin: 20px 0;
          font-size: 0.875rem;
        }

        .chart-container {
          height: 200px;
          margin-bottom: 20px;
        }

        .status-legend {
          display: grid;
          gap: 8px;
          margin-top: 20px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          padding: 8px;
          background: #f9fafb;
          border-radius: 4px;
        }

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin-right: 8px;
        }

        .legend-label {
          flex: 1;
          font-weight: 500;
          color: #374151;
        }

        .legend-count {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .filter-container {
          margin-bottom: 20px;
        }

        .filter-container label {
          margin-right: 8px;
          font-size: 0.875rem;
          color: #374151;
        }

        .filter-container select {
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.875rem;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
        }

        .card-content {
          min-height: 300px;
        }
      `}</style>
    </div>
  );
};

export default JobStatusCard;
